import React, { useEffect, useState } from 'react';
import fileDownload from 'js-file-download';
import html2canvas from 'html2canvas';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import {
	FileDown,
	UserPlus,
	TrendingUp,
	Users,
	MessageCircle,
	BarChart3,
	ChevronDown,
} from 'lucide-react';
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
    LineChart,
    Line,
    Legend,
	ResponsiveContainer,
} from 'recharts';
import AssignedLeadsList from '@CommonRepresentationalDisplay/AssignedLeadsList';
import WhatsappChatsList from '@CommonRepresentationalDisplay/WhatsappChatsList';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import LeadsAnalyticService from '@Helpers/URLHelpers/LeadsAnalyticService';
import ErrorHandler from '@Root/ErrorHandler';
import './DashboardPage.scss';

function StatCard({ title, value, icon, color, description }) {
	return (
		<div className={`dashboard-stat dashboard-stat--${color}`}>
			<div className='dashboard-stat__icon'>{icon}</div>
			<div className='dashboard-stat__content'>
				<span className='dashboard-stat__label'>{title}</span>
				<strong className='dashboard-stat__value'>{value ?? 0}</strong>
				{description && (
					<span className='dashboard-stat__description'>{description}</span>
				)}
			</div>
		</div>
	);
}

function CustomTooltip({ active, payload, label }) {
	if (!active || !payload?.length) {
		return null;
	}
	return (
		<div className='dashboard-tooltip'>
			<p className='dashboard-tooltip__label'>{label}</p>
			{payload.map((item) => (
				<div className='dashboard-tooltip__row' key={item.dataKey}>
					<span
						className='dashboard-tooltip__dot'
						style={{
							background: item.color,
						}}
					/>
					<span>{item.name}</span>
					<strong>{item.value}</strong>
				</div>
			))}
		</div>
	);
}

export default function DashboardPage() {

	const navigate = useNavigate();
	const [cursor, setCursor] = useState('auto');
	const [isExporting, setIsExporting] = useState(false);
	const [analytic, setAnalytic] = useState(null);
	const [chartData, setChartData] = useState([]);
	const [chartFilter, setChartFilter] = useState(6);
	const { activeUser } = useReduxActiveUser();
	const { currentProjectId } = useReduxProject();
	const leadsAnalyticService = new LeadsAnalyticService();
	const errorHandler = new ErrorHandler();
	const now = new Date();

	const dateStr = now.toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
	});

	useEffect(() => {
		if (!currentProjectId) {
			return;
		}
		renderData();
	}, [currentProjectId]);

	const renderData = async () => {
		try {
			let response =
				await leadsAnalyticService.getAnalyticByProjectId(currentProjectId);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				const sortedAsc = response.chartData
					.slice()
					.sort((a, b) => a.timeStamp - b.timeStamp);
				const lastSixMonths = sortedAsc.slice(-6);
				setChartData(lastSixMonths);
				setAnalytic(response);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
	};

	const getPreviousMonthTimestamp = (n) => {
		const currentDate = new Date();
		const currentMonthStart = new Date(
			currentDate.getFullYear(),
			currentDate.getMonth(),
			1,
		);
		const previousMonthDate = new Date(currentMonthStart);
		previousMonthDate.setMonth(previousMonthDate.getMonth() - Number(n));
		return previousMonthDate.getTime();
	};

	const getChartData = async (timeStamp, newValue) => {
		try {
			let response = await leadsAnalyticService.getAnalyticByCharts(
				currentProjectId,
				timeStamp,
			);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				const sortedAsc = response
					.slice()
					.sort((a, b) => a.timeStamp - b.timeStamp);
				const list = sortedAsc.slice(-Number(newValue));
				setChartData(list);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
	};

	const handleChartAnalytic = (value) => {
		const newValue = Number(value);
		setChartFilter(newValue);
		const timeStamp = getPreviousMonthTimestamp(newValue);
		getChartData(timeStamp, newValue);
	};

	const dataURLtoFile = (dataurl) => {
		const arr = dataurl?.split(',');
		if (!arr?.length) {
			return null;
		}
		const mime = arr[0]?.match(/:(.*?);/)?.[1];
		const bstr = atob(arr[1]);
		let n = bstr.length;
		const u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		return new File([u8arr], 'dashboard.png', {
			type: mime,
		});
	};

	return (
		<div
			className='dashboard'
			id='divToExport'
			style={{
				cursor,
			}}
		>
			<header className='dashboard-header'>
				<div className='dashboard-header__content'>
					<div className='dashboard-header__eyebrow'>
						<BarChart3 size={13} />
						DASHBOARD
					</div>
					<h1>
						Welcome back, <span>{activeUser?.fullName || 'there'}</span>
						<span className='dashboard-header__wave'>👋</span>
					</h1>
					<p>{dateStr} · Here's your business overview.</p>
				</div>
				<div className='dashboard-header__actions'>
					<button
						className='dashboard-btn dashboard-btn--primary'
						onClick={() => navigate('/home/whatsapp/campaigns')}
					>
						<UserPlus size={14} />
						Start Campaign
					</button>
				</div>
			</header>
			<section className='dashboard-stats'>
				<StatCard
					title='Total Leads'
					value={analytic?.totalLeads}
					icon={<Users size={17} />}
					color='blue'
					description='All leads'
				/>
				<StatCard
					title='Messages Sent'
					value={analytic?.totalMsgSend}
					icon={<MessageCircle size={17} />}
					color='green'
					description='WhatsApp messages'
				/>
			</section>
			{/* LIST PANELS */}
			<section className='dashboard-panels'>
				<AssignedLeadsList />
				<WhatsappChatsList />
			</section>
			<section className='dashboard-chart-card'>
				<div className='dashboard-chart-card__header'>
					<div className='dashboard-chart-card__title'>
						<div className='dashboard-chart-card__icon'>
							<BarChart3 size={16} />
						</div>
						<div>
							<h2>Growth Overview</h2>
							<p>Leads and WhatsApp activity</p>
						</div>
					</div>
					<div className='dashboard-chart-filter'>
						<select
							value={chartFilter}
							onChange={(e) => handleChartAnalytic(e.target.value)}
						>
							<option value={6}>Last 6 months</option>
							<option value={3}>Last 3 months</option>
							<option value={1}>Last 30 days</option>
						</select>
						<ChevronDown size={13} />
					</div>
				</div>
				<div className='dashboard-chart-summary'>
					<div className='dashboard-chart-summary__item'>
						<span className='dashboard-chart-summary__dot dashboard-chart-summary__dot--blue' />
						<div>
							<span>Leads</span>
							<strong>{analytic?.totalLeads ?? 0}</strong>
						</div>
					</div>
					<div className='dashboard-chart-summary__item'>
						<span className='dashboard-chart-summary__dot dashboard-chart-summary__dot--green' />
						<div>
							<span>WhatsApp</span>
							<strong>{analytic?.totalMsgSend ?? 0}</strong>
						</div>
					</div>
				</div>
				<div className='dashboard-chart-area'>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 10,
                                right: 15,
                                left: -15,
                                bottom: 0,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#eef2f6"
                            />

                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: '#98a2b3',
                                    fontSize: 8,
                                }}
                            />
                            <YAxis
                                yAxisId="leads"
                                orientation="left"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: '#2563eb',
                                    fontSize: 8,
                                }}
                            />
                            <YAxis
                                yAxisId="whatsapp"
                                orientation="right"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: '#16a34a',
                                    fontSize: 8,
                                }}
                            />
                            <Tooltip />
                            <Legend
                                wrapperStyle={{
                                    fontSize: '8px',
                                    fontWeight: 500,
                                }}/>
                            <Line
                                yAxisId="leads"
                                type="monotone"
                                dataKey="leads"
                                name="Leads"
                                fontSize={8}
                                stroke="#2563eb"
                                strokeWidth={2}
                                dot={false}
                            />

                            <Line
                                yAxisId="whatsapp"
                                type="monotone"
                                dataKey="whatsapp"
                                name="WhatsApp"
                                stroke="#16a34a"
                                strokeWidth={2}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
				</div>
			</section>
		</div>
	);
}
